// SYSTEM_ROUTE: /academics
import { useRef, useState } from 'react';
import type { Admission, FundingSource, OfferStatus, TouchpointChannel, EducationProgram, EducationProgramInput, Course } from '@life-os/app-core';
import {
  committedFundingMinor,
  pipelineFundingMinor,
  fundingGapMinor,
  studyFundingNeedMinor,
  daysUntil,
  formatMinor,
  TOUCHPOINT_CHANNELS,
  creditSummaryTotal,
} from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { useAsync } from '../lib/useAsync.js';
import { seedOwnerData } from '../lib/seed.js';

const OFFER_TONE: Record<OfferStatus, ChipTone> = {
  researching: 'muted',
  applied: 'neutral',
  conditional: 'warn',
  unconditional: 'primary',
  accepted: 'primary',
  deferred: 'neutral',
  declined: 'muted',
  rejected: 'danger',
};

const FUNDING_TONE: Record<string, ChipTone> = {
  planned: 'muted',
  open: 'neutral',
  applied: 'neutral',
  awarded: 'primary',
  disbursed: 'primary',
  rejected: 'danger',
};

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(100, Math.round((part / whole) * 100));
}

function AdmissionCard({ admission, funding }: { admission: Admission; funding: readonly FundingSource[] }) {
  const linked = funding; // (all funding shown against the mission for now)
  const need = studyFundingNeedMinor(admission);
  const committed = committedFundingMinor(linked);
  const pipeline = pipelineFundingMinor(linked);
  const gap = fundingGapMinor(need, committed);
  const cur = admission.currency;
  const nowIso = new Date().toISOString();
  const depDays = admission.depositDueDate ? daysUntil(admission.depositDueDate, nowIso) : null;

  return (
    <Pane
      title={`${admission.institution} — ${admission.programme}`}
      hint={admission.intakeTerm}
      action={<Chip tone={OFFER_TONE[admission.offerStatus]}>{admission.offerStatus}</Chip>}
    >
      <Stack>
        {admission.depositDueDate ? (
          <div className="lc-focus" style={{ padding: '16px 18px' }}>
            <div className="lc-focus__eyebrow">Critical path · deposit</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div className="lc-focus__title" style={{ fontSize: 20 }}>
                Pay {formatMinor(admission.depositMinor, cur)} to secure the offer
              </div>
              <Chip tone={depDays !== null && depDays <= 7 ? 'danger' : 'warn'}>
                {depDays === null
                  ? admission.depositDueDate
                  : depDays < 0
                    ? `${Math.abs(depDays)} days overdue`
                    : depDays === 0
                      ? 'due today'
                      : `${depDays} days left`}
              </Chip>
            </div>
            <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
              Due {admission.depositDueDate} · counts toward tuition
            </div>
          </div>
        ) : null}

        <div className="lc-stats">
          <div className="lc-stat">
            <span className="lc-stat__value" style={{ fontSize: 20 }}>{formatMinor(admission.tuitionMinor, cur)}</span>
            <span className="lc-stat__label">Tuition</span>
          </div>
          <div className="lc-stat">
            <span className="lc-stat__value" style={{ fontSize: 20 }}>{formatMinor(admission.livingEstimateMinor, cur)}</span>
            <span className="lc-stat__label">Living (yr est.)</span>
          </div>
          <div className="lc-stat">
            <span className="lc-stat__value" style={{ fontSize: 20 }}>{formatMinor(need, cur)}</span>
            <span className="lc-stat__label">Total need</span>
          </div>
          <div className="lc-stat">
            <span className="lc-stat__value" style={{ fontSize: 20, color: gap > 0 ? 'var(--lc-warn)' : 'var(--lc-primary)' }}>
              {formatMinor(gap, cur)}
            </span>
            <span className="lc-stat__label">Gap to fund</span>
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--lc-text-xs)', color: 'var(--lc-on-surface-variant)', marginBottom: 6 }}>
            <span>Funding secured</span>
            <span>{formatMinor(committed, cur)} secured · {formatMinor(pipeline, cur)} in pipeline</span>
          </div>
          <div style={{ height: 10, borderRadius: 999, background: 'var(--lc-surface-container-high)', overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: `${pct(committed, need)}%`, background: 'var(--lc-primary)' }} />
            <div style={{ width: `${pct(pipeline, need)}%`, background: 'var(--lc-accent-soft)' }} />
          </div>
        </div>

        {admission.conditions.length > 0 ? (
          <div>
            <div className="lc-label" style={{ marginBottom: 6 }}>Conditions</div>
            <Stack>
              {admission.conditions.map((c, i) => (
                <CompactRow key={i} title={c} />
              ))}
            </Stack>
          </div>
        ) : null}
      </Stack>
    </Pane>
  );
}

function FundingCard({ source }: { source: FundingSource }) {
  const services = useServices();
  const { refresh } = useStore();
  const [note, setNote] = useState('');
  const [channel, setChannel] = useState<TouchpointChannel>('visit');
  const [action, setAction] = useState(source.nextAction);
  const [actionDate, setActionDate] = useState(source.nextActionDate ?? '');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const nowIso = new Date().toISOString();
  const naDays = source.nextActionDate ? daysUntil(source.nextActionDate, nowIso) : null;

  const logUpdate = async () => {
    if (note.trim().length === 0) return;
    setBusy(true);
    try {
      await services.academics.logTouchpoint(source.id, {
        on: new Date().toISOString().slice(0, 10),
        channel,
        note: note.trim(),
      });
      setNote('');
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const saveNextStep = async () => {
    setBusy(true);
    try {
      await services.academics.setFundingNextAction(
        source.id,
        action.trim(),
        actionDate.trim().length > 0 ? actionDate : null,
      );
      setEditing(false);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Pane
      title={source.name}
      hint={`${source.sourceKind}${source.amountMinor > 0 ? ` · up to ${formatMinor(source.amountMinor, source.currency)}` : ''}${source.fullyFunded ? ' · fully funded' : ''}`}
      action={<Chip tone={FUNDING_TONE[source.status] ?? 'neutral'}>{source.status}</Chip>}
    >
      <Stack>
        {/* Next step — the one proactive move that pushes this source forward. */}
        {!editing ? (
          <CompactRow
            title={source.nextAction.trim().length > 0 ? source.nextAction : 'No next step set'}
            subtitle={source.nextActionDate ? `By ${source.nextActionDate}` : 'Set the next move so it shows on Today'}
            meta={
              naDays !== null ? (
                <Chip tone={naDays < 0 ? 'danger' : naDays <= 7 ? 'warn' : 'neutral'}>
                  {naDays < 0 ? `${Math.abs(naDays)}d overdue` : naDays === 0 ? 'today' : `${naDays}d`}
                </Chip>
              ) : (
                <Button variant="ghost" onClick={() => setEditing(true)}>Set next step</Button>
              )
            }
            onClick={source.nextAction.trim().length > 0 ? () => setEditing(true) : undefined}
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--lc-stack-sm)' }}>
            <input
              className="lc-cmdbar"
              style={{ flex: '2 1 220px' }}
              value={action}
              placeholder="Next step (e.g. Confirm application open date)"
              onChange={(e) => setAction(e.target.value)}
              aria-label="Next step"
            />
            <input
              className="lc-cmdbar"
              style={{ flex: '1 1 150px' }}
              type="date"
              value={actionDate}
              onChange={(e) => setActionDate(e.target.value)}
              aria-label="Next step date"
            />
            <Button variant="primary" onClick={() => void saveNextStep()}>{busy ? 'Saving…' : 'Save'}</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        )}

        {/* Touchpoint history — proof of proactive pursuit, newest first. */}
        {source.touchpoints.length > 0 ? (
          <div>
            <div className="lc-label" style={{ marginBottom: 6 }}>Pursuit log</div>
            <Stack>
              {source.touchpoints.slice(0, 4).map((t, i) => (
                <CompactRow
                  key={i}
                  title={t.note}
                  subtitle={t.on}
                  meta={<Chip tone="muted">{t.channel}</Chip>}
                />
              ))}
            </Stack>
          </div>
        ) : null}

        {/* Log a new touchpoint — capture the doing; measurement comes later. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--lc-stack-sm)' }}>
          <select
            className="lc-cmdbar"
            style={{ flex: '0 0 auto' }}
            value={channel}
            onChange={(e) => setChannel(e.target.value as TouchpointChannel)}
            aria-label="Contact channel"
          >
            {TOUCHPOINT_CHANNELS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={note}
            placeholder="Log an update (e.g. Visited office — applications open mid-July)"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void logUpdate(); }}
            aria-label="Log update"
          />
          <Button variant="primary" onClick={() => void logUpdate()}>{busy ? 'Logging…' : 'Log update'}</Button>
        </div>
      </Stack>
    </Pane>
  );
}

function prettyCategory(c: string): string {
  return c
    .replace(/^UC_/, 'University Core · ')
    .replace(/^UE_/, 'University Elective · ')
    .replace(/^PROGRAMME_/, 'Programme ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function EducationCard({ program }: { program: EducationProgram }) {
  const [showCourses, setShowCourses] = useState(false);
  const courses = program.courses;
  // Group courses by term for a calm transcript view.
  const byTerm = new Map<string, Course[]>();
  for (const c of courses) {
    const key = c.term ?? 'Other';
    const list = byTerm.get(key) ?? [];
    list.push(c);
    byTerm.set(key, list);
  }
  const terms = [...byTerm.keys()];

  return (
    <Pane
      title={`${program.institution} — ${program.qualification}`}
      hint={program.branch ?? program.field ?? undefined}
      action={program.cgpa != null ? <Chip tone="primary">CGPA {program.cgpa}/10</Chip> : <Chip tone="neutral">{program.status}</Chip>}
    >
      <Stack>
        <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
          {program.title}
          {program.registrationNumber ? ` · Reg ${program.registrationNumber}` : ''}
          {program.startDate ? ` · ${program.startDate.slice(0, 4)}–${program.endDate ? program.endDate.slice(0, 4) : 'now'}` : ''}
        </div>

        <div className="lc-stats">
          <div className="lc-stat"><span className="lc-stat__value" style={{ fontSize: 20 }}>{program.cgpa ?? '—'}</span><span className="lc-stat__label">CGPA</span></div>
          <div className="lc-stat"><span className="lc-stat__value" style={{ fontSize: 20 }}>{program.totalCredits ?? creditSummaryTotal(program.creditSummary)}</span><span className="lc-stat__label">Credits</span></div>
          <div className="lc-stat"><span className="lc-stat__value" style={{ fontSize: 20 }}>{courses.length}</span><span className="lc-stat__label">Courses</span></div>
          <div className="lc-stat"><span className="lc-stat__value" style={{ fontSize: 20 }}>{terms.length}</span><span className="lc-stat__label">Terms</span></div>
        </div>

        {program.creditSummary.length > 0 ? (
          <div>
            <div className="lc-label" style={{ marginBottom: 6 }}>Credit breakdown</div>
            <Stack>
              {program.creditSummary.map((l) => (
                <CompactRow key={l.category} title={prettyCategory(l.category)} meta={<Chip tone="neutral">{l.credits} cr</Chip>} />
              ))}
            </Stack>
          </div>
        ) : null}

        {courses.length > 0 ? (
          <div>
            <Button variant="ghost" onClick={() => setShowCourses((s) => !s)}>
              {showCourses ? 'Hide courses' : `Show ${courses.length} courses`}
            </Button>
            {showCourses ? (
              <Stack>
                {terms.map((term) => (
                  <div key={term}>
                    <div className="lc-label" style={{ margin: '8px 0 4px' }}>{term}</div>
                    <Stack>
                      {byTerm.get(term)!.map((c, i) => (
                        <CompactRow
                          key={`${c.code ?? i}`}
                          title={c.title}
                          subtitle={`${c.code ?? ''}${c.credits != null ? ` · ${c.credits} cr` : ''}${c.ltpj && c.ltpj !== '0-0-0-0' ? ` · L-T-P-J ${c.ltpj}` : ''}`}
                          meta={c.category ? <Chip tone="muted">{prettyCategory(c.category)}</Chip> : undefined}
                        />
                      ))}
                    </Stack>
                  </div>
                ))}
              </Stack>
            ) : null}
          </div>
        ) : null}
      </Stack>
    </Pane>
  );
}

export function AcademicsScreen() {
  const services = useServices();
  const { revision, refresh, data } = useStore();
  const [busy, setBusy] = useState(false);
  const [eduMsg, setEduMsg] = useState<string | null>(null);
  const eduFile = useRef<HTMLInputElement>(null);

  const profile = useAsync(() => services.academics.getProfile(), [revision]);
  const admissions = useAsync(() => services.academics.listAdmissions(), [revision]);
  const funding = useAsync(() => services.academics.listFunding(), [revision]);
  const programs = useAsync(() => services.academics.listEducationPrograms(), [revision]);

  const prof = profile.value;
  const adms = admissions.value ?? [];
  const funds = funding.value ?? [];
  const progs = programs.value ?? [];
  const empty = !profile.loading && !prof && adms.length === 0 && progs.length === 0;

  const loadMine = async () => {
    setBusy(true);
    try {
      await seedOwnerData(services, data.graph);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const importDegree = async (file: File) => {
    setEduMsg(null);
    try {
      const raw = JSON.parse(await file.text()) as { degree?: Record<string, unknown>; creditSummary?: unknown[]; courses?: unknown[] };
      const d = raw.degree;
      if (!d) { setEduMsg('That file has no "degree" section.'); return; }
      const input: EducationProgramInput = {
        institution: String(d.institution ?? 'Unknown'),
        qualification: String(d.qualification ?? 'Degree'),
        title: String(d.title ?? d.qualification ?? 'Degree'),
        field: (d.field as string) ?? null,
        branch: (d.branch as string) ?? null,
        school: (d.school as string) ?? null,
        registrationNumber: (d.registrationNumber as string) ?? null,
        institutionEmail: (d.institutionEmail as string) ?? null,
        city: (d.city as string) ?? null,
        country: (d.country as string) ?? null,
        website: (d.website as string) ?? null,
        startDate: (d.startDate as string) ?? null,
        endDate: (d.endDate as string) ?? null,
        totalCredits: (d.totalCredits as number) ?? null,
        cgpa: (d.cgpa as number) ?? null,
        gradeLabel: (d.gradeLabel as string) ?? null,
        status: (d.status as EducationProgramInput['status']) ?? 'completed',
        creditSummary: (raw.creditSummary as EducationProgramInput['creditSummary']) ?? [],
        courses: (raw.courses as EducationProgramInput['courses']) ?? [],
      };
      await services.academics.importEducationProgram(input);
      setEduMsg(`Imported ${input.title} — ${input.courses?.length ?? 0} courses.`);
      refresh();
    } catch (err) {
      setEduMsg(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (eduFile.current) eduFile.current.value = '';
    }
  };

  return (
    <>
      <ScreenHeader
        title="Academics & Funding"
        subtitle="Education, university admissions, deadlines, and how study gets funded."
        actions={prof ? <Chip tone="primary">{prof.displayName}</Chip> : undefined}
      />

      {empty ? (
        <Pane title="Set up your academics" hint="Loads your real Edinburgh data — editable, local-first.">
          <Stack>
            <EmptyState title="No academic data yet">
              Load your profile, the Edinburgh MSc admission (conditional offer, £1,500 deposit due 30 June,
              tuition + living), your funding sources, and the deposit reminder — all as real records you can edit.
            </EmptyState>
            <div>
              <Button variant="primary" onClick={() => void loadMine()}>
                {busy ? 'Loading…' : 'Load my Edinburgh data'}
              </Button>
            </div>
          </Stack>
        </Pane>
      ) : null}

      {prof ? (
        <Pane title="Profile">
          <Stack>
            <CompactRow title={prof.displayName} subtitle={prof.headline} meta={prof.location ? <Chip tone="muted">{prof.location}</Chip> : undefined} />
            {prof.education.map((e, i) => (
              <CompactRow
                key={i}
                title={`${e.qualification} · ${e.field}`}
                subtitle={`${e.institution}${e.endYear ? ` · ${e.endYear}` : ''}`}
                meta={<Chip tone={e.status === 'completed' ? 'primary' : e.status === 'planned' ? 'warn' : 'neutral'}>{e.status.replace('_', ' ')}</Chip>}
              />
            ))}
          </Stack>
        </Pane>
      ) : null}

      {progs.map((p) => (
        <EducationCard key={String(p.id)} program={p} />
      ))}

      <Pane title="Education records" hint="Import your degree, courses, and credits (from the v1 migration)">
        <Stack>
          <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
            Load <code>academics-v2.json</code> (produced by the offline migration of your VIT Bhopal
            bachelor's data). Re-importing updates the same degree — it never duplicates.
          </div>
          <input
            ref={eduFile}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void importDegree(f); }}
            aria-label="Degree data file"
          />
          <div>
            <Button variant="primary" onClick={() => eduFile.current?.click()}>Import degree data</Button>
          </div>
          {eduMsg ? <Chip tone="primary">{eduMsg}</Chip> : null}
        </Stack>
      </Pane>

      {adms.map((a) => (
        <AdmissionCard key={String(a.id)} admission={a} funding={funds} />
      ))}

      {funds.length > 0 ? (
        <>
          <ScreenHeader title="Funding pursuit" subtitle="Each source, its next move, and a log of what you've already done to chase it." />
          {funds.map((f) => (
            <FundingCard key={String(f.id)} source={f} />
          ))}
        </>
      ) : null}
    </>
  );
}
